import { useCallback, useEffect, useState } from 'react';
import { CheckCircle2, CreditCard, ExternalLink, RefreshCw } from 'lucide-react';
import { ApiError, apiFetch } from '../api/client.ts';
import { Button } from './Button.tsx';
import { Card } from './Card.tsx';

// The balance on completion: the second half of a fixed-fee project,
// charged to the card the client saved when they paid the deposit.
//
// A billing admin presses it, deliberately, with the amount and the card
// in front of them - charging a client is never a side effect of some
// other click. A decline is shown in words with the client's invoice
// link (where they can pay with another card, or approve the payment
// their bank asked about), and Retry charges the SAME invoice again.

interface Balance {
  eligible: boolean;
  reason: string | null;
  amountCents: number | null;
  status: 'not_charged' | 'pending' | 'open' | 'paid' | 'void' | 'uncollectible';
  card: { brand: string | null; last4: string | null } | null;
  hostedInvoiceUrl: string | null;
  lastError: string | null;
  paidAt: string | null;
  attempts: number;
}

const money = (cents: number): string =>
  (cents / 100).toLocaleString('en-US', { style: 'currency', currency: 'USD' });

const cardLabel = (card: Balance['card']): string =>
  card?.last4
    ? `${card.brand ? card.brand[0]!.toUpperCase() + card.brand.slice(1) : 'Card'} ending ${card.last4}`
    : 'the saved card';

export function ProjectBalanceCard({
  projectId,
  onPaid,
}: {
  projectId: string;
  /** The project's "payment collected" flag changes when this is paid. */
  onPaid: () => void | Promise<void>;
}): React.ReactElement | null {
  const [balance, setBalance] = useState<Balance | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const data = await apiFetch<{ balance: Balance }>(`/api/projects/${projectId}/balance`);
      setBalance(data.balance);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Could not load the balance.');
    }
  }, [projectId]);

  useEffect(() => {
    void load();
  }, [load]);

  async function run(action: 'charge' | 'refresh'): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      const data = await apiFetch<{ balance: Balance }>(`/api/projects/${projectId}/balance/${action}`, {
        method: 'POST',
      });
      const wasPaid = balance?.status === 'paid';
      setBalance(data.balance);
      if (data.balance.status === 'paid' && !wasPaid) await onPaid();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Something went wrong. Nothing was charged.');
    } finally {
      setBusy(false);
      setConfirming(false);
    }
  }

  if (!balance && !error) return null;

  return (
    <Card>
      <h2 className="flex items-center gap-2 text-base font-semibold text-[var(--color-header)]">
        <CreditCard className="h-4 w-4 text-[var(--color-muted)]" aria-hidden="true" />
        Balance on completion
      </h2>

      {error && <p className="mt-3 text-sm text-[var(--color-danger)]">{error}</p>}

      {balance && !balance.eligible && (
        <p className="mt-2 text-sm text-[var(--color-muted)]">{balance.reason}</p>
      )}

      {balance?.eligible && balance.amountCents !== null && (
        <div className="mt-3 space-y-3">
          <p className="text-sm text-[var(--color-body)]">
            <span className="text-2xl font-bold tabular-nums text-[var(--color-header)]">
              {money(balance.amountCents)}
            </span>{' '}
            due on substantial completion, to {cardLabel(balance.card)} - the card the client paid the
            deposit with.
          </p>

          {balance.status === 'paid' ? (
            <p className="flex items-center gap-2 rounded-[var(--radius-button)] border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-900">
              <CheckCircle2 className="h-4 w-4 shrink-0" aria-hidden="true" />
              Paid{balance.paidAt ? ` on ${new Date(balance.paidAt).toLocaleDateString()}` : ''}. The project is
              marked payment collected.
            </p>
          ) : (
            <>
              {balance.lastError && (
                <div className="rounded-[var(--radius-button)] border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
                  <p className="font-semibold">Not paid yet.</p>
                  <p className="mt-0.5">{balance.lastError}</p>
                </div>
              )}

              {confirming ? (
                <div className="flex flex-wrap items-center gap-2 rounded-[var(--radius-button)] border border-[var(--color-border)] bg-[var(--color-surface)] p-3">
                  <span className="text-sm font-medium text-[var(--color-header)]">
                    Charge {money(balance.amountCents)} to {cardLabel(balance.card)} now?
                  </span>
                  <Button onClick={() => void run('charge')} disabled={busy}>
                    {busy ? 'Charging…' : 'Yes, charge it'}
                  </Button>
                  <Button variant="outline" onClick={() => setConfirming(false)} disabled={busy}>
                    Cancel
                  </Button>
                </div>
              ) : (
                <div className="flex flex-wrap gap-2">
                  <Button onClick={() => setConfirming(true)} disabled={busy}>
                    {balance.status === 'not_charged'
                      ? `Charge ${money(balance.amountCents)}`
                      : 'Retry the charge'}
                  </Button>
                  {balance.status !== 'not_charged' && (
                    <Button variant="outline" onClick={() => void run('refresh')} disabled={busy}>
                      <RefreshCw className="h-4 w-4" aria-hidden="true" />
                      Check if the client paid
                    </Button>
                  )}
                </div>
              )}
            </>
          )}

          {balance.hostedInvoiceUrl && (
            <p className="text-sm">
              <a
                href={balance.hostedInvoiceUrl}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-1 font-semibold text-[var(--color-t3-blue)] hover:underline"
              >
                The client&apos;s invoice <ExternalLink className="h-3.5 w-3.5" aria-hidden="true" />
              </a>
              {balance.status !== 'paid' && (
                <span className="text-[var(--color-muted)]">
                  {' '}
                  - send them this to pay with another card or approve the payment.
                </span>
              )}
            </p>
          )}
        </div>
      )}
    </Card>
  );
}
